import React, { useState, useEffect } from "react";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import { getConfig } from "../../../config/production";
import { PageOpening, Section, SectionTitle, MarginNote, Prose } from "../../public/Journal";
import Reveal from "../../public/Reveal";

const STATE_LABELS = {
    operational: "Operational",
    degraded: "Degraded performance",
    down: "Service down",
};

// Small typeset mark per state: ink square, stamp outline, solid stamp.
function StateMark({ status }) {
    const cls =
        status === "operational"
            ? "bg-ink dark:bg-paper"
            : status === "degraded"
            ? "border-[1.5px] border-stamp"
            : status === "down"
            ? "bg-stamp"
            : "border border-ink/40 dark:border-paper/40";
    return <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 shrink-0 ${cls}`} />;
}

function StateText({ status }) {
    return (
        <span
            className={`inline-flex items-center gap-2.5 font-plex-mono text-[12px] uppercase tracking-[0.14em] ${
                status === "operational" ? "text-ink dark:text-paper" : "text-stamp"
            }`}
        >
            <StateMark status={status} />
            {STATE_LABELS[status] || "Unknown"}
        </span>
    );
}

const fmtTime = (d) => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const fmtDate = (d) => d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });

function Status() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    useEffect(() => {
        fetchStatus();
        // Auto-refresh every 10 minutes (600000ms)
        const interval = setInterval(fetchStatus, 600000);
        return () => clearInterval(interval);
    }, []);

    const fetchStatus = async () => {
        try {
            const apiUrl = getConfig('API_URL');
            const response = await fetch(`${apiUrl}/status`);
            const data = await response.json();
            setServices(data.services || []);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Failed to fetch status:", error);
            setServices([]);
        } finally {
            setLoading(false);
        }
    };

    const overallStatus = services.every(s => s.status === "operational")
        ? "operational"
        : services.some(s => s.status === "down")
        ? "down"
        : "degraded";

    const unavailable = !loading && services.length === 0;

    const summary = loading
        ? "Reading the ledger…"
        : unavailable
        ? "The status ledger couldn't be reached just now."
        : overallStatus === "operational"
        ? "All systems are operating normally. You can access all features without any issues."
        : overallStatus === "down"
        ? "One or more services are down. Affected entries are marked in red below."
        : "Some services are running with degraded performance. Affected entries are marked below.";

    return (
        <StandalonePageLayout>
            <PageOpening
                section="§ Ledger"
                label="System status"
                title="The service ledger."
                intro={summary}
            >
                <dl className="flex flex-wrap gap-x-10 gap-y-3 font-plex-mono text-[12px] uppercase tracking-[0.14em] text-ink/60 dark:text-paper/60">
                    <div className="flex gap-3">
                        <dt>Overall</dt>
                        <dd>{loading || unavailable ? "—" : <StateText status={overallStatus} />}</dd>
                    </div>
                    <div className="flex gap-3">
                        <dt>Last updated</dt>
                        <dd className="text-ink dark:text-paper">{fmtDate(lastUpdated)} · {fmtTime(lastUpdated)}</dd>
                    </div>
                    <div className="flex gap-3">
                        <dt>Refresh</dt>
                        <dd className="text-ink dark:text-paper">Every 10 min</dd>
                    </div>
                </dl>
            </PageOpening>

            <Section
                number="01"
                label="Services"
                aside={<MarginNote>Entries refresh automatically every ten minutes. Times are local to your browser.</MarginNote>}
            >
                {/* Ledger header */}
                <div className="hidden grid-cols-[110px_1fr_auto] gap-6 border-b border-ink/70 pb-3 font-plex-mono text-[11px] uppercase tracking-[0.18em] text-ink/50 sm:grid dark:border-paper/60 dark:text-paper/50">
                    <span>Entry</span>
                    <span>Service</span>
                    <span>State</span>
                </div>

                {loading ? (
                    <p className="border-b border-ink/15 py-6 font-plex-mono text-[13px] text-ink/60 dark:border-paper/15 dark:text-paper/60">
                        Reading the ledger<span className="animate-pulse">…</span>
                    </p>
                ) : unavailable ? (
                    <div className="border-b border-ink/15 py-8 dark:border-paper/15">
                        <p className="font-news text-[26px] leading-tight">Unable to fetch system status.</p>
                        <p className="mt-2 font-plex text-[15px] text-ink/65 dark:text-paper/65">
                            Please check your connection or try again later.
                        </p>
                        <button
                            type="button"
                            onClick={() => { setLoading(true); fetchStatus(); }}
                            className="link-underline mt-5 font-plex-mono text-[12px] uppercase tracking-[0.16em] text-stamp"
                        >
                            Try again
                        </button>
                    </div>
                ) : (
                    <ol>
                        {services.map((service, index) => (
                            <Reveal
                                as="li"
                                key={index}
                                delay={index * 60}
                                className="grid grid-cols-1 gap-2 border-b border-ink/15 py-5 sm:grid-cols-[110px_1fr_auto] sm:items-baseline sm:gap-6 dark:border-paper/15"
                            >
                                <span className="font-plex-mono text-[12px] text-ink/45 dark:text-paper/45">
                                    No. {String(index + 1).padStart(3, "0")}
                                </span>
                                <span className="font-news text-[24px] leading-tight">{service.name}</span>
                                <StateText status={service.status} />
                            </Reveal>
                        ))}
                    </ol>
                )}
            </Section>

            <Section number="02" label="About this page">
                <SectionTitle>About system status</SectionTitle>
                <Prose className="mt-6">
                    <p>
                        This page shows the operational status of JPRO services.
                        Status information is automatically updated every 10 minutes.
                    </p>
                    <p>
                        If you&rsquo;re experiencing issues not reflected here, please check your internet connection
                        or contact support.
                    </p>
                </Prose>
            </Section>
        </StandalonePageLayout>
    );
}

export default Status;
